import type { Layout, Layouts } from "react-grid-layout";
import {
  BREAKPOINTS,
  HOME_BLOCK_MAP,
  sanitizeLayouts,
  type Breakpoint,
  type StoredHomeLayout,
} from "./home-blocks";

/** Where a block sits in a hand-authored default. Resize bounds come from the
 *  block's own definition, so they are never restated here. */
type Placement = { i: string; x: number; y: number; w: number; h: number };

/**
 * Desktop: an eight-column work column beside a four-column reference rail. The
 * two lead cards are wide and tall enough to explain themselves in full, while
 * Catalogue and Build stay one row high and so read as briefs.
 */
const LG: Placement[] = [
  { i: "verify", x: 0, y: 0, w: 8, h: 2 },
  { i: "links", x: 8, y: 0, w: 4, h: 2 },
  { i: "clip", x: 0, y: 2, w: 8, h: 2 },
  { i: "whats-new", x: 8, y: 2, w: 4, h: 3 },
  { i: "data-catalogue", x: 0, y: 4, w: 4, h: 1 },
  { i: "build", x: 4, y: 4, w: 4, h: 1 },
];

/**
 * Tablet: six columns cannot hold an eight-column card and a rail side by side,
 * so the rail drops below the core functions and the page becomes one column of
 * full-width cards.
 */
const MD: Placement[] = [
  { i: "verify", x: 0, y: 0, w: 6, h: 2 },
  { i: "clip", x: 0, y: 2, w: 6, h: 2 },
  { i: "data-catalogue", x: 0, y: 4, w: 3, h: 1 },
  { i: "build", x: 3, y: 4, w: 3, h: 1 },
  { i: "links", x: 0, y: 5, w: 6, h: 2 },
  { i: "whats-new", x: 0, y: 7, w: 6, h: 3 },
];

/** Mobile: a single stack in the same reading order. */
const XS: Placement[] = [
  { i: "verify", x: 0, y: 0, w: 1, h: 2 },
  { i: "clip", x: 0, y: 2, w: 1, h: 2 },
  { i: "data-catalogue", x: 0, y: 4, w: 1, h: 1 },
  { i: "build", x: 0, y: 5, w: 1, h: 1 },
  { i: "links", x: 0, y: 6, w: 1, h: 2 },
  { i: "whats-new", x: 0, y: 8, w: 1, h: 3 },
];

const PLACEMENTS: Record<Breakpoint, Placement[]> = { lg: LG, md: MD, xs: XS };

const HIDDEN_BY_DEFAULT = ["explore", "download", "batch", "apps-and-apis"];

function toLayout(placement: Placement, bp: Breakpoint): Layout {
  const bounds = HOME_BLOCK_MAP[placement.i]?.sizes[bp];
  return { ...placement, minW: bounds?.minW, minH: bounds?.minH };
}

/**
 * The arrangement a user lands on before they have edited anything.
 *
 * DS-5841 will eventually pick between several arrangements based on what the
 * user told us during onboarding, so this function is the one place that choice
 * belongs: add a second set of placement tables and select between them here.
 * Today there is one, because the onboarding signal is not readable yet.
 */
export function defaultStoredLayout(): StoredHomeLayout {
  const layouts: Layouts = {};
  for (const bp of BREAKPOINTS) {
    layouts[bp] = PLACEMENTS[bp].map((p) => toLayout(p, bp));
  }
  return { version: 3, layouts, hidden: [...HIDDEN_BY_DEFAULT] };
}

/** Load a persisted layout, falling back to the default for anything not v3. */
export function migrate(raw: unknown): StoredHomeLayout {
  if (
    raw &&
    typeof raw === "object" &&
    (raw as StoredHomeLayout).version === 3 &&
    typeof (raw as StoredHomeLayout).layouts === "object"
  ) {
    const stored = raw as StoredHomeLayout;
    return {
      version: 3,
      layouts: sanitizeLayouts(stored.layouts),
      hidden: Array.isArray(stored.hidden) ? stored.hidden : [],
    };
  }
  return defaultStoredLayout();
}
